// src/pages/tw/base64Page_tw.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../base64Page.css";

function Base64Page() {
  const [darkMode, setDarkMode] = useState(false);

  // CSR 基準 canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Base64 編碼器/解碼器",
      description:
        "使用 TooLoves 工具，將文字編碼為 Base64，或將 Base64 解碼為 UTF-8 文字",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "Base64, Base64 Encoder, Base64 Decoder, Base64 編碼器, Base64 解碼器, 編碼, 解碼, 文字轉換, URL 安全, TooLoves, 免費",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "TWD",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // 依深/淺色模式同步 meta theme-color
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  // 隱藏的檔案輸入參照
  const fileInputRef = useRef(null);

  // 輸入參照
  const inputRef = useRef(null);

  // 狀態
  const [mode, setMode] = useState("encode"); // encode | decode
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [decodedBytes, setDecodedBytes] = useState(null); // 解碼結果保存
  const [urlSafe, setUrlSafe] = useState(false);
  const [error, setError] = useState("");

  // 檔案按鈕 -> 觸發隱藏的 input
  const onPickClick = () => {
    if (!fileInputRef.current) return;
    try {
      fileInputRef.current.value = "";
    } catch {}
    fileInputRef.current.click();
  };

  // 選擇檔案後載入文字
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setInputText(text);
    } catch (err) {
      setError("讀取檔案時發生錯誤。");
    }
  };

  // textarea 自動高度調整
  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    autoResize(inputRef.current);
  }, [inputText]);

  // Base64 工具
  const bytesToBase64 = (bytes) => {
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      const sub = bytes.subarray(i, i + chunk);
      binary += String.fromCharCode(...sub);
    }
    return btoa(binary);
  };

  const base64ToBytes = (b64) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  };

  const encodeUtf8ToBase64 = (text, { urlSafe }) => {
    const bytes = new TextEncoder().encode(text);
    let b64 = bytesToBase64(bytes);
    if (urlSafe) {
      b64 = b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
    }
    return b64;
  };

  const normalizeBase64Input = (raw, { urlSafe }) => {
    let s = raw || "";
    // 移除空白
    s = s.replace(/\s+/g, "");
    if (urlSafe) s = s.replace(/-/g, "+").replace(/_/g, "/");
    // padding
    const pad = s.length % 4;
    if (pad > 0) s = s + "=".repeat(4 - pad);
    return s;
  };

  const decodeBase64ToUtf8 = (raw, { urlSafe }) => {
    const normalized = normalizeBase64Input(raw, { urlSafe });
    const bytes = base64ToBytes(normalized);
    const text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return { text, bytes };
  };

  // 可讀性格式化：每 76 字元換行 + 縮排（僅編碼模式）
  const wrapAndIndent = (str, width = 76, indent = "  ") => {
    if (!str) return "";
    const out = [];
    for (let i = 0; i < str.length; i += width) {
      out.push(indent + str.slice(i, i + width));
    }
    return out.join("\n");
  };

  const getDisplayOutput = () => {
    if (!outputText) return "";
    // 僅對 Base64 編碼結果套用 76 字元換行 + 縮排（解碼文字保持原樣）
    if (mode === "encode") {
      return wrapAndIndent(outputText, 76, "  ");
    }
    return outputText;
  };

  // 轉換處理
  const transform = () => {
    setError("");
    try {
      if (mode === "encode") {
        const b64 = encodeUtf8ToBase64(inputText, { urlSafe });
        setOutputText(b64);
        setDecodedBytes(null);
      } else {
        const { text, bytes } = decodeBase64ToUtf8(inputText, { urlSafe });
        setOutputText(text);
        setDecodedBytes(bytes);
      }
    } catch (err) {
      setOutputText("");
      setDecodedBytes(null);
      setError("輸入無效或轉換過程中發生錯誤。");
    }
  };

  useEffect(() => {
    transform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputText, mode, urlSafe]);

  // 複製到剪貼簿（依顯示格式）
  const handleCopy = async () => {
    try {
      const data = getDisplayOutput() || "";
      await navigator.clipboard.writeText(data);
    } catch (err) {
      setError("複製到剪貼簿失敗。");
    }
  };

  // 下載為 TXT（依顯示格式）
  const handleDownload = () => {
    try {
      const data = getDisplayOutput() || "";
      const blob = new Blob([data], { type: "text/plain;charset=utf-8" });
      const filename = mode === "encode" ? "base64.txt" : "decoded.txt";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("下載時發生錯誤。");
    }
  };

  const handleReset = () => {
    setInputText("");
    setOutputText("");
    setDecodedBytes(null);
    setMode("encode");
    setUrlSafe(false);
    setError("");
    requestAnimationFrame(() => {
      autoResize(inputRef.current);
    });
  };

  const handleSwap = () => {
    if (!outputText) return;
    if (mode === "encode") {
      setInputText(outputText);
      setMode("decode");
    } else {
      setInputText(outputText);
      setMode("encode");
    }
  };

  const isOutputEmpty = !outputText || outputText.trim() === "";
  const displayOutput = getDisplayOutput();

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "zh-TW" }}>
        <title>Base64 編碼器/解碼器 | TooLoves</title>
        <meta
          name="description"
          content="使用此線上工具，將文字編碼為 Base64，或將 Base64 字串解碼為 UTF-8 文字"
        />
        <meta
          name="keywords"
          content="Base64, 編碼, 解碼, URL 安全, 文字轉換, TooLoves, 免費"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 初始 theme-color，會在執行階段由 useEffect 同步 */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="zh_TW" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="Base64 編碼器/解碼器 | TooLoves" />
        <meta
          property="og:description"
          content="輕鬆在文字與 Base64 之間轉換"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-base64.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Base64 編碼器/解碼器 | TooLoves" />
        <meta
          name="twitter:description"
          content="輕鬆在文字與 Base64 之間轉換"
        />
        <meta name="twitter:image" content="/og-base64.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* 上方 App Bar */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/tw" className="brand-link" aria-label="前往 TooLoves 首頁">
            TooLoves
          </Link>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="mode-toggle" onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? "淺色模式" : "深色模式"}
            </button>
          </div>
        </div>
      </header>

      {/* 內容 */}
      <main
        className="container"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          paddingLeft: 16,
          paddingRight: 16,
        }}
      >
        <section className="generator-card">
          <h2 className="card-title">Base64 編碼器/解碼器</h2>
          <p className="card-subtitle">
            可將文字編碼為 Base64，或將 Base64 解碼為 UTF-8 文字。
          </p>

          <form className="form" onSubmit={(e) => e.preventDefault()}>
            {/* 檔案上傳（隱藏 input + 按鈕） */}
            <div className="input-row">
              <input
                ref={fileInputRef}
                id="b64-file-input"
                type="file"
                accept="text/plain,.txt"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={onPickClick}
                aria-label="選擇文字檔案"
                className="btn btn-primary"
              >
                選擇檔案
              </button>
            </div>

            {/* 輸入 */}
            <div className="input-row" style={{ marginTop: 8 }}>
              <div className="io-wrap" style={{ width: "100%" }}>
                <textarea
                  ref={inputRef}
                  className="text-input text-inputtextarea mono"
                  placeholder={
                    mode === "encode"
                      ? "在此輸入文字或選擇檔案。"
                      : "請在此貼上 Base64 字串。"
                  }
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  rows={4}
                  style={{
                    width: "100%",
                    display: "block",
                    maxWidth: "1200px",
                    margin: "0 auto",
                  }}
                  spellCheck={false}
                />
              </div>
            </div>

            {/* 選項 */}
            <div className="options-row" style={{ gap: 16 }}>
              <div className="field">
                <label className="form-label">模式</label>
                <select value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="encode">編碼（文字 → Base64）</option>
                  <option value="decode">解碼（Base64 → 文字）</option>
                </select>
              </div>

              <div className="field switch">
                <label className="form-label">URL-safe</label>
                <input
                  type="checkbox"
                  checked={urlSafe}
                  onChange={(e) => setUrlSafe(e.target.checked)}
                />
              </div>
            </div>
          </form>

          {/* 輸出 */}
          <div className="metadata-section" style={{ paddingTop: 8 }}>
            <div className="block">
              <div className="label-row">
                <label htmlFor="b64-output" className="label-strong">
                  輸出結果
                </label>
              </div>

              <div
                id="b64-output"
                role="textbox"
                aria-readonly="true"
                tabIndex={0}
                className="mono-box"
                style={{ whiteSpace: "pre-wrap" }} // 保留換行/縮排
              >
                {displayOutput ||
                  (mode === "encode"
                    ? "編碼結果將顯示在此"
                    : "解碼結果將顯示在此")}
              </div>

              {error && (
                <p className="hint" role="alert" style={{ color: "#ef4444", marginTop: 8 }}>
                  {error}
                </p>
              )}
            </div>

            {/* 下方操作：複製/下載 + 交換/重設 */}
            <div className="actions">
              <button
                className="btn-primary"
                onClick={handleCopy}
                type="button"
                aria-label="複製輸出結果"
                disabled={isOutputEmpty}
              >
                複製
              </button>
              <button
                className="btn-primary"
                onClick={handleDownload}
                type="button"
                aria-label="下載輸出結果 txt"
                disabled={isOutputEmpty}
              >
                下載 TXT
              </button>
              <button className="btn-secondary" onClick={handleSwap} type="button">
                交換
              </button>
              <button className="btn-secondary" onClick={handleReset} type="button">
                重設
              </button>
            </div>

            {/* 解碼時的位元組長度提示 */}
            {mode === "decode" && decodedBytes && (
              <p className="hint" style={{ marginTop: 8 }}>
                位元組長度：{decodedBytes.byteLength.toLocaleString()} 位元組
              </p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="法律資訊連結">
          <Link to="/terms" className="footer-link">
            服務條款
          </Link>
          <span className="footer-sep" aria-hidden="true">
            ·
          </span>
          <Link to="/privacy" className="footer-link">
            隱私權政策
          </Link>
        </nav>
      </footer>
    </div>
  );
}

export default Base64Page;